import { useCallback, useState, useRef } from 'react'
import classNames from 'classnames'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form/dist/index.ie11'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes, faCheck } from '@fortawesome/pro-regular-svg-icons'
// import { useEffect } from '@storybook/addons'
import formatError from '../../../utilities/formatError'
import { formatPercentage, formatNumber } from '../utils'
import { Stack } from '../../stack'
import { Button } from '../../button'
import { TextField } from '../../textfield'
import { Alert } from '../../alert'
import { Row, Col } from '../../grid'
import { Heading } from '../../typography/Heading'
import computeOffsetTop from '../../../utilities/computeTopOffset'
import useEnabledOnClient from '../../../utilities/useEnabledOnClient'
import * as styles from '../CalculatorModule.module.scss'

const validationSchema = {
  downPayment: {
    min: 0,
    max: 999999999,
  },
  termMonths: {
    min: 1,
    max: 360,
  },
  rebate: {
    min: 0,
    max: 999999999,
  },
  leasePayment: {
    min: 0,
    max: 999999999,
  },
  leaseDeposit: {
    min: 0,
    max: 999999999,
  },
  purchasePrice: {
    min: 0,
    max: 999999999,
  },
  salesTax: {
    min: 0,
    max: 75,
  },
  purchaseCosts: {
    min: 0,
    max: 999999999,
  },
  loanRate: {
    min: 0,
    max: 40,
  },
  residualValue: {
    min: 0,
    max: 999999999,
  },
}

const AutoLeaseCalculator = () => {
  const [t, i18n] = useTranslation()
  const [language, countryCode] = i18n.language.split('-')
  const [calcResults, setCalcResults] = useState()
  const [storedFormData, setStoredFormData] = useState()
  // Our API doesn't support sync form submissions and so we need Javascript to submit the form asynchronously by issuing an XHR. Therefore, we need to ensure that the button is initially disabled and gets re-enabled once all Javascript has loaded on the client.
  const disabled = useEnabledOnClient()

  const { register, errors, handleSubmit } = useForm({
    mode: 'onBlur',
    defaultValues: {
      downPayment: 0,
      termMonths: 36,
      rebate: 0,
      leasePayment: 0,
      leaseDeposit: 0,
      purchasePrice: 0,
      salesTax: 5,
      purchaseCosts: 0,
      loanRate: 6,
      residualValue: 0,
    },
  })

  const [inProgress, setInProgress] = useState(false)
  const [alert, showAlert] = useState(null)
  const dismissAlert = useCallback(() => showAlert(null), [])
  const resultsContainer = useRef(null)
  const resultsHeader = useRef(null)

  const handleSubmitValid = async formData => {
    setInProgress(true)
    showAlert(false)
    setStoredFormData(formData)

    try {
      const url = 'https://www.calcxml.com/rest/aut01'

      // Documentation
      // https://www.calcxml.com/documentation/rest/Aut01JSONRequest.htm

      const payload = {
        username: process.env.CALCXML_USERNAME,
        password: process.env.CALCXML_PASSWORD,
        language,
        countryCode,
        chartWidth: 900,
        chartHeight: 400,
        barColor1: '63B5E4',
        barColor2: '84BD00',
        barColor3: 'FCC841',
        // barColor4: '#ffffff',
        // barColor5: '#ffffff',
        // barColor6: '#ffffff',
        downPayment: formData.downPayment,
        termMonths: formData.termMonths,
        rebate: formData.rebate,
        leasePayment: formData.leasePayment,
        leaseDeposit: formData.leaseDeposit,
        purchasePrice: formData.purchasePrice,
        salesTax: formatPercentage(formData.salesTax),
        purchaseCosts: formData.purchaseCosts,
        loanRate: formatPercentage(formData.loanRate),
        residualValue: formData.residualValue,
      }

      const response = await axios.post(url, payload)

      if (response.status !== 200 || Boolean(response.data.error)) {
        throw new Error(`Unexpected status code: ${response.status} ${response.statusText}`)
      }

      setInProgress(false)
      setCalcResults(response.data)
      window.scrollTo({
        top: computeOffsetTop(resultsContainer.current) - 20,
        behavior: 'smooth',
      })
      resultsHeader.current.focus()
    } catch (error) {
      setInProgress(false)
      showAlert({
        type: 'error',
        title: t('AutoLeaseCalculator.error', {
          defaultValue: 'Error',
        }),
        message: error.message,
      })
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
    }
  }

  return (
    <div>
      {alert && (
        <Row>
          <Col xs={12}>
            <Alert variant={alert.type} title={alert.title} onDismiss={dismissAlert}>
              {alert.message}
            </Alert>
          </Col>
        </Row>
      )}
      <form
        action="/"
        method="POST"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit(handleSubmitValid)}
        className={alert ? styles.HasAlert : ''}
      >
        <fieldset className={classNames(styles.Fieldset, styles.withMargin)}>
          <legend className={styles.Legend}>
            {t('AutoLeaseCalculator.firstLegend', {
              defaultValue: 'Common assumptions',
            })}
          </legend>
          <Row>
            <Col xs={12} lg={6}>
              <TextField
                label={t('AutoLeaseCalculator.downPaymentLabel', {
                  defaultValue: 'Down payment (capital reduction) ($)',
                })}
                id="downPayment"
                name="downPayment"
                type="number"
                min="0"
                ref={register(validationSchema.downPayment)}
                error={formatError(errors.downPayment, validationSchema.downPayment, t)}
                autoFocus
                autoSelect
              />
            </Col>

            <Col xs={12} lg={6}>
              <TextField
                label={t('AutoLeaseCalculator.termMonthsLabel', {
                  defaultValue: 'Number of months (1 to 360)',
                })}
                id="termMonths"
                name="termMonths"
                type="number"
                min="0"
                max="360"
                ref={register(validationSchema.termMonths)}
                error={formatError(errors.termMonths, validationSchema.termMonths, t)}
                autoSelect
              />
            </Col>
          </Row>

          <Row>
            <Col xs={12} lg={6}>
              <TextField
                label={t('AutoLeaseCalculator.rebateLabel', {
                  defaultValue: 'Rebate amount ($)',
                })}
                id="rebate"
                name="rebate"
                type="number"
                min="0"
                ref={register(validationSchema.rebate)}
                error={formatError(errors.rebate, validationSchema.rebate, t)}
                autoSelect
              />
            </Col>
          </Row>
        </fieldset>

        <fieldset className={classNames(styles.Fieldset, styles.withMargin)}>
          <legend className={styles.Legend}>
            {t('AutoLeaseCalculator.secondLegend', {
              defaultValue: 'Lease information',
            })}
          </legend>
          <Row>
            <Col xs={12} lg={6}>
              <TextField
                label={t('AutoLeaseCalculator.leasePaymentLabel', {
                  defaultValue: 'Monthly payment amount ($)',
                })}
                id="leasePayment"
                name="leasePayment"
                type="number"
                min="0"
                ref={register(validationSchema.leasePayment)}
                error={formatError(errors.leasePayment, validationSchema.leasePayment, t)}
                autoSelect
              />
            </Col>

            <Col xs={12} lg={6}>
              <TextField
                label={t('AutoLeaseCalculator.leaseDepositLabel', {
                  defaultValue: 'Security deposit/leasing fee ($)',
                })}
                id="leaseDeposit"
                name="leaseDeposit"
                type="number"
                min="0"
                ref={register(validationSchema.leaseDeposit)}
                error={formatError(errors.leaseDeposit, validationSchema.leaseDeposit, t)}
                autoSelect
              />
            </Col>
          </Row>
        </fieldset>

        <fieldset className={classNames(styles.Fieldset, styles.withMargin)}>
          <legend className={styles.Legend}>
            {t('AutoLeaseCalculator.thirdLegend', {
              defaultValue: 'Purchase information',
            })}
          </legend>
          <Row>
            <Col xs={12} lg={6}>
              <TextField
                label={t('AutoLeaseCalculator.purchasePriceLabel', {
                  defaultValue: 'Purchase price ($)',
                })}
                id="purchasePrice"
                name="purchasePrice"
                type="number"
                min="0"
                ref={register(validationSchema.purchasePrice)}
                error={formatError(errors.purchasePrice, validationSchema.purchasePrice, t)}
                autoSelect
              />
            </Col>

            <Col xs={12} lg={6}>
              <TextField
                label={t('AutoLeaseCalculator.salesTaxLabel', {
                  defaultValue: 'Sales tax rate (0% to 75%)',
                })}
                id="salesTax"
                name="salesTax"
                type="number"
                min="0"
                max="75"
                ref={register(validationSchema.salesTax)}
                error={formatError(errors.salesTax, validationSchema.salesTax, t)}
                autoSelect
              />
            </Col>
          </Row>

          <Row>
            <Col xs={12} lg={6}>
              <TextField
                label={t('AutoLeaseCalculator.purchaseCostsLabel', {
                  defaultValue: 'Other upfront costs ($)',
                })}
                id="purchaseCosts"
                name="purchaseCosts"
                type="number"
                min="0"
                ref={register(validationSchema.purchaseCosts)}
                error={formatError(errors.purchaseCosts, validationSchema.purchaseCosts, t)}
                autoSelect
              />
            </Col>
            <Col xs={12} lg={6}>
              <TextField
                label={t('AutoLeaseCalculator.loanRateLabel', {
                  defaultValue: 'Annual percentage rate on loan (0% to 40%)',
                })}
                id="loanRate"
                name="loanRate"
                type="number"
                min="1"
                max="40"
                ref={register(validationSchema.loanRate)}
                error={formatError(errors.loanRate, validationSchema.loanRate, t)}
                autoSelect
              />
            </Col>
          </Row>

          <Row>
            <Col xs={12} lg={6}>
              <TextField
                label={t('AutoLeaseCalculator.residualValueLabel', {
                  defaultValue: 'Market value of vehicle at end of loan ($)',
                })}
                id="residualValue"
                name="residualValue"
                type="number"
                min="0"
                ref={register(validationSchema.residualValue)}
                error={formatError(errors.residualValue, validationSchema.residualValue, t)}
                autoSelect
              />
            </Col>
          </Row>
        </fieldset>

        <Stack alignment="center" className={styles.Submit}>
          <Button
            type="submit"
            disabled={inProgress || disabled}
            loading={inProgress}
            alt={t('AutoLeaseCalculator.calculateAlt', {
              defaultValue: 'Calculate to view results',
            })}
          >
            {t('AutoLeaseCalculator.calculate', {
              defaultValue: 'Calculate',
            })}
          </Button>

          {alert && alert.type === 'error' && (
            <span className={styles.ShortMessage}>
              <FontAwesomeIcon icon={faTimes} className={styles.TimesIcon} />
              {t('AutoLeaseCalculator.couldNotCalculate', {
                defaultValue: 'Sorry, could not calculate',
              })}
            </span>
          )}

          {alert && alert.type === 'success' && (
            <span className={styles.ShortMessage}>
              <FontAwesomeIcon icon={faCheck} className={styles.CheckIcon} />
              {t('AutoLeaseCalculator.calculationComplete', {
                defaultValue: 'Calculation complete. See below for results!',
              })}
            </span>
          )}
        </Stack>
      </form>

      <div ref={resultsContainer} />

      {calcResults && (
        <div className={styles.Results}>
          <Heading
            className={styles.ResultsHeading}
            element="h3"
            size={3}
            tabIndex="0"
            forwardedRef={resultsHeader}
          >
            {t('AutoLeaseCalculator.results', {
              defaultValue: 'Results',
            })}
          </Heading>
          <p>{calcResults.responseText}</p>

          <Heading className={styles.ResultsHeading} element="h3" size={3}>
            {t('AutoLeaseCalculator.summaryTable', {
              defaultValue: 'Summary table',
            })}
          </Heading>

          <table className={styles.ResultsTable} role="grid">
            <caption>
              {t('AutoLeaseCalculator.summaryTable', {
                defaultValue: 'Lease Summary table',
              })}
            </caption>
            <tbody>
              <tr>
                <th colSpan="3" scope="col" className="section">
                  {t('AutoLeaseCalculator.costOfLeasing', {
                    defaultValue: 'Cost of leasing',
                  })}
                </th>
              </tr>
              <tr>
                <th scope="row">
                  {t('AutoLeaseCalculator.totalPayments', {
                    defaultValue: 'Total payments',
                  })}
                </th>
                <td />
                <td>{calcResults.totalLeasePayments}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('AutoLeaseCalculator.securityDepositLeasingFee', {
                    defaultValue: 'Security deposit/leasing fee',
                  })}
                </th>
                <td className={styles.ResultsTableCenter}> + </td>
                <td>${formatNumber(storedFormData.leaseDeposit)}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('AutoLeaseCalculator.downPayment', {
                    defaultValue: 'Down payment (capital reduction)',
                  })}
                </th>
                <td className={styles.ResultsTableCenter}> + </td>
                <td>${formatNumber(storedFormData.downPayment)}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('AutoLeaseCalculator.rebate', {
                    defaultValue: 'Rebate',
                  })}
                </th>
                <td className={styles.ResultsTableCenter}> - </td>
                <td>${formatNumber(storedFormData.rebate)}</td>
              </tr>
              <tr>
                <th scope="row">
                  <b>
                    {t('AutoLeaseCalculator.totalCostLease', {
                      defaultValue: 'Total cost of lease',
                    })}
                  </b>
                </th>
                <td className={styles.ResultsTableCenter}> = </td>
                <td>{calcResults.totalLease}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('AutoLeaseCalculator.averageYearlyCostLease', {
                    defaultValue: 'Average yearly cost of lease',
                  })}
                </th>
                <td />
                <td>{calcResults.yearlyLease}</td>
              </tr>
              <tr>
                <td />
                <td />
                <td />
              </tr>
            </tbody>
          </table>

          <table className={styles.ResultsTable} role="grid">
            <caption>
              {t('AutoLeaseCalculator.summaryTable', {
                defaultValue: 'Purchas Summary table',
              })}
            </caption>
            <tbody>
              <tr>
                <th colSpan="3" className="section">
                  {t('AutoLeaseCalculator.purchasingCost', {
                    defaultValue: 'Cost of purchasing',
                  })}
                </th>
              </tr>
              <tr>
                <th scope="row">
                  {t('AutoLeaseCalculator.purchasePrice', {
                    defaultValue: 'Purchase price',
                  })}
                </th>
                <td />
                <td>${formatNumber(storedFormData.purchasePrice)}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('AutoLeaseCalculator.salesTax', {
                    defaultValue: 'Sales tax',
                  })}
                </th>
                <td className={styles.ResultsTableCenter}> + </td>
                <td>
                  $
                  {formatNumber(
                    formatPercentage(storedFormData.salesTax) *
                      parseFloat(storedFormData.purchasePrice)
                  )}
                </td>
              </tr>
              <tr>
                <th scope="row">
                  {t('AutoLeaseCalculator.otherUpfrontCosts', {
                    defaultValue: 'Other upfront costs',
                  })}
                </th>
                <td className={styles.ResultsTableCenter}> + </td>
                <td>${formatNumber(storedFormData.purchaseCosts)}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('AutoLeaseCalculator.interest', {
                    defaultValue: 'Interest',
                  })}
                </th>
                <td className={styles.ResultsTableCenter}> + </td>
                <td>{calcResults.interestPaid}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('AutoLeaseCalculator.rebate', {
                    defaultValue: 'Rebate',
                  })}
                </th>
                <td className={styles.ResultsTableCenter}> - </td>
                <td>${formatNumber(storedFormData.rebate)}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('AutoLeaseCalculator.marketValue', {
                    defaultValue: 'Market value at end of loan term',
                  })}
                </th>
                <td className={styles.ResultsTableCenter}> - </td>
                <td>${formatNumber(storedFormData.residualValue)}</td>
              </tr>
              <tr>
                <th scope="row">
                  <b>
                    {t('AutoLeaseCalculator.totalPurchasingCost', {
                      defaultValue: 'Total cost of purchasing',
                    })}
                  </b>
                </th>
                <td className={styles.ResultsTableCenter}> = </td>
                <td>{calcResults.totalPurchase}</td>
              </tr>
              <tr>
                <th scope="row">
                  {t('AutoLeaseCalculator.averageYearlyCost', {
                    defaultValue: 'Average yearly cost of purchasing',
                  })}
                </th>
                <td />
                <td>{calcResults.yearlyPurchase}</td>
              </tr>
            </tbody>
          </table>

          <Heading className={styles.ResultsHeading} element="h3" size={3}>
            {t('AutoLeaseCalculator.graph', {
              defaultValue: 'Graph',
            })}
          </Heading>
          <img
            src={calcResults.chartUrl}
            alt={t('AutoLeaseCalculator.graphOfResults', {
              defaultValue: 'Graph of results',
            })}
          />
        </div>
      )}
    </div>
  )
}

export { AutoLeaseCalculator }
export default AutoLeaseCalculator
